import { useRef, type KeyboardEvent, type ReactNode } from 'react'

interface CellProps {
  children: ReactNode
  align?: 'right' | 'center'
  /** Leading adornment rendered INSIDE the interactive box (e.g. the primary
   *  column's type glyph), so the highlight ring includes it. */
  prefix?: ReactNode
  /** Activate the cell — open the editor (write kinds) or flip (boolean).
   *  Only wired when the cell is editable. */
  onActivate?: () => void
  /** When `onActivate` fires on pointer: 'dblclick' (default — overlay editors)
   *  or 'click' (boolean — a checkbox flips on a single click). Enter on a
   *  focused cell always activates, regardless of this. */
  activateOn?: 'click' | 'dblclick'
}

/**
 * Generic interactive cell container (ADR-0024 container). EVERY column type
 * renders through this — via RenderedCell — so the interaction is defined once:
 *
 *  - click focuses/selects the cell (cobalt ring)
 *  - double-click or Enter opens the editor (write kinds) or flips (boolean)
 *  - when content is clipped, hovering shows the full value via a native tooltip
 *
 * Attio model: click = select/focus only. No read-only expand popover that would
 * intercept the double-click that opens the editor (the bug that table-fixed
 * made pervasive once cells clipped broadly).
 */
export function Cell({ children, align, prefix, onActivate, activateOn = 'dblclick' }: CellProps) {
  const ref = useRef<HTMLButtonElement>(null)

  function handleClick() {
    const el = ref.current
    if (!el) return
    el.focus() // select/focus the cell — never opens a read-only overlay
    if (activateOn === 'click' && onActivate) onActivate() // boolean single-click flip
  }

  function handleKeyDown(e: KeyboardEvent) {
    // Enter on a focused cell activates (edit / flip), suppressing the native
    // button click-from-Enter so it can't double-fire a toggle.
    if (e.key === 'Enter' && onActivate) {
      e.preventDefault()
      onActivate()
    }
  }

  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'

  // With a prefix, prefix and value share one flex row and the VALUE
  // truncates itself: an inline prefix beside an inline-flex chip made the
  // block's ellipsis swallow the whole chip ("…") once the label was long.
  const body = prefix != null ? (
    <span className="flex min-w-0 items-center">
      <span className="mr-1.5 inline-flex shrink-0">{prefix}</span>
      <span data-cell-value className="min-w-0 truncate">{children}</span>
    </span>
  ) : (
    <span data-cell-value>{children}</span>
  )

  return (
    <button
      ref={ref}
      type="button"
      // A cell's single click only focuses it (editing is a double-click), so it lets the ROW's click
      // through (open the record). A single-click toggle (a boolean) keeps its own click.
      data-row-click={activateOn === 'click' && onActivate ? undefined : 'pass'}
      onClick={handleClick}
      onDoubleClick={activateOn === 'dblclick' ? onActivate : undefined}
      onKeyDown={handleKeyDown}
      onMouseEnter={(e) => {
        const el = e.currentTarget
        if (el.scrollWidth > el.clientWidth + 1) {
          const valueEl = el.querySelector('[data-cell-value]')
          el.title = valueEl?.textContent ?? ''
        } else {
          el.title = ''
        }
      }}
      onMouseLeave={() => {
        if (ref.current) ref.current.title = ''
      }}
      data-testid="cell"
      className={`block w-full truncate rounded-sm px-2 py-1.5 text-sm text-foreground outline-none focus:relative focus:z-10 focus:ring-2 focus:ring-ring ${alignClass}`}
    >
      {body}
    </button>
  )
}
